import React, { useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Mimshak } from "../../../interfaces/DTO/mislaka_xml_models";
import { useSearchParams } from "react-router-dom";

import { useViewedTypeStore, usePresentationModeStore } from "../../../stores/selectedToViewStore";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { formatAge } from "../../../utils/namesFormater";
import { ContactDetailsDrawer } from "../../CRM/contacts/ContactDetailsDrawer";
import { FamilyInfoForCard } from "../../CRM/contacts/ContactPortalAccessCard";
import ConsultingContactInfo from "../ConsultingContactInfo";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { usePartialUpdateContact } from "../../../hooks/crm/useContacts";
import { usePartialUpdateFamily } from "../../../hooks/crm/useFamilies";
import InteractiveIcon from "../../../shared/UIParts/InteractiveIcon";
import { iconEntityFilterCategory, EntityFilterCategory, formatEntityFilterCategory, colorEntityFilterCategory } from "../../../utils/featuresFormatter";
import PresentationIcon from "@mui/icons-material/Slideshow";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";


interface ConsultingHeaderProps {
  id: number;
  name: string;
  contacts: Contact[];
  mimshaks: Mimshak[];
  allMimshakData?: MimshakData[];
  viewedType: "USER" | "FAMILY";
  handleFamilyMemberClick?: (userId: number) => void;
  familyInfo?: FamilyInfoForCard;
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
}

export const ConsultingHeader: React.FC<ConsultingHeaderProps> = ({
  id,
  name,
  contacts,
  mimshaks,
  allMimshakData,
  viewedType,
  handleFamilyMemberClick,
  familyInfo,
  selectedPeriodType,
}) => {

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const [searchParams, setSearchParams] = useSearchParams();
  const isPresentationMode = searchParams.get("presentation") === "true";
  const setPresentationState = usePresentationModeStore((state) => state.setPresentationState);
  const clearPresentationState = usePresentationModeStore((state) => state.clearPresentationState);

  const togglePresentationMode = () => {
    const newParams = new URLSearchParams(searchParams);
    if (isPresentationMode) {
      // Exiting presentation mode - clear frozen state
      clearPresentationState();
      newParams.delete("presentation");
      newParams.delete("id");
      newParams.delete("type");
    } else {
      // Entering presentation mode - save frozen state
      setPresentationState(id, viewedType, contacts);
      newParams.set("presentation", "true");
      if (id) {
        newParams.set("id", id.toString());
      }
      if (viewedType) {
        newParams.set("type", viewedType);
      }
    }
    setSearchParams(newParams);
  };

  const updateContact = usePartialUpdateContact();
  const updateFamily = usePartialUpdateFamily();

  const toggleEntityFlag = ({
    entityType,
    id,
    field,
    value,
  }: {
    entityType: "contact" | "family";
    id: number;
    field: string;
    value: any;
  }) => {
    const payload = { [field]: value };
    if (entityType === "contact") {
      updateContact.mutate({ id, fields: payload });
    } else {
      updateFamily.mutate({ id, fields: payload });
    }
  };

  const userTitle = useMemo(() => {
    const userPrivacyData = contacts[0];
    const age = formatAge(userPrivacyData?.date_of_birth);
    const namePart = `${name} | גיל: ${age}`;

    if (!userPrivacyData) return namePart;

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        {namePart}
 {(contacts.length === 1 && isLargeScreen) && (
        <Tooltip title="העתק ת.ז">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(userPrivacyData?.israeli_id || "");
            }}
            size="small"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

        <InteractiveIcon
          icon={iconEntityFilterCategory(EntityFilterCategory.HasQuestion)}
          tooltip={formatEntityFilterCategory(EntityFilterCategory.HasQuestion)}
          color={colorEntityFilterCategory(EntityFilterCategory.HasQuestion)}
          active={userPrivacyData.has_question ?? false}
          onToggle={(newValue) =>
            toggleEntityFlag({
              entityType: "contact",
              id: userPrivacyData.id,
              field: "has_question",
              value: newValue,
            })
          }
        />

        <InteractiveIcon
          icon={iconEntityFilterCategory(EntityFilterCategory.RequiresAttention)}
          tooltip={formatEntityFilterCategory(EntityFilterCategory.RequiresAttention)}
          color={colorEntityFilterCategory(EntityFilterCategory.RequiresAttention)}
          active={userPrivacyData.requires_attention ?? false}
          onToggle={(newValue) =>
            toggleEntityFlag({
              entityType: "contact",
              id: userPrivacyData.id,
              field: "requires_attention",
              value: newValue,
            })
          }
        />

        <InteractiveIcon
          icon={iconEntityFilterCategory(EntityFilterCategory.Favorite)}
          tooltip={formatEntityFilterCategory(EntityFilterCategory.Favorite)}
          color={colorEntityFilterCategory(EntityFilterCategory.Favorite)}
          active={(userPrivacyData.favorites_rank ?? 0) > 0}
          onToggle={(newValue) =>
            toggleEntityFlag({
              entityType: "contact",
              id: userPrivacyData.id,
              field: "favorites_rank",
              value: newValue ? 1 : 0,
            })
          }
        />

        {isLargeScreen && (
          <Tooltip title={isPresentationMode ? "יציאה ממצב תצוגה" : "מצב תצוגה"}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                togglePresentationMode();
              }}
              size="large"
              sx={{
                color: isPresentationMode ? theme.palette.primary.main : theme.palette.text.secondary,
                padding: "10px",
              }}
            >
              {isPresentationMode ? (
                <PauseCircleOutlineIcon fontSize="large" />
              ) : (
                <PresentationIcon fontSize="large" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [name, contacts, isPresentationMode, isLargeScreen, theme]);

  const familyUserNames = useMemo(
    () =>
      contacts.map((contact) => {
        const userName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
        const age = formatAge(contact?.date_of_birth);

        return (
          <ListItem
            key={contact.id}
            disablePadding
            sx={{
              width: "auto",
              flex: "0 1 auto",
              display: "inline-flex",
              padding: "4px 8px",
            }}
          >
            <ListItemButton
              onClick={() => handleFamilyMemberClick?.(contact.id!)}
              sx={{
                minWidth: "unset",
                padding: "0px 3px",
                marginTop: "0px",
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      fontWeight: "500",
                    }}
                  >
                    {`${userName} (${age})`}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      }),
    [contacts]
  );

  return (
    <Grid container spacing={2} alignItems="right" rowSpacing={0}>
      {viewedType === "USER" && (
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography
            variant="h5"
            onClick={openDrawer}
            sx={{
              cursor: "pointer",
              whiteSpace: "nowrap",
            
             
              fontWeight: "bold",
              gap: 1,
              fontSize: isLargeScreen ? "28px" : "24px",
              lineHeight: isLargeScreen ? "32px" : "28px",
            }}
          >
            <IconButton onClick={openDrawer}>
              <MoreVertIcon />
            </IconButton>
            {userTitle}
          </Typography>
        </Grid>
      )}

      {viewedType === "FAMILY" && (
        <>
          <Typography
            variant="h5"
            onClick={openDrawer}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
              cursor: "pointer",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              gap: 1,
              fontSize: isLargeScreen ? "28px" : "24px",
              lineHeight: isLargeScreen ? "32px" : "28px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Tooltip title="עוד אפשרויות">
              <IconButton onClick={openDrawer}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
              {name}
              {contacts.length === 1 && hovered && (
                <>
                  <InteractiveIcon
                    icon={iconEntityFilterCategory(EntityFilterCategory.HasQuestion)}
                    tooltip={formatEntityFilterCategory(EntityFilterCategory.HasQuestion)}
                    active={contacts[0].has_question ?? false}
                    onToggle={(newValue) =>
                      toggleEntityFlag({
                        entityType: "contact",
                        id: contacts[0].id!,
                        field: "has_question",
                        value: newValue,
                      })
                    }
                  />
                  <InteractiveIcon
                    icon={iconEntityFilterCategory(EntityFilterCategory.RequiresAttention)}
                    tooltip={formatEntityFilterCategory(EntityFilterCategory.RequiresAttention)}
                    active={contacts[0].requires_attention ?? false}
                    onToggle={(newValue) =>
                      toggleEntityFlag({
                        entityType: "contact",
                        id: contacts[0].id!,
                        field: "requires_attention",
                        value: newValue,
                      })
                    }
                  />
                </>
              )}
            </Box>
          </Typography>

          <Grid size={{ xs: 12, md: 8 }} display="flex" justifyContent="flex-start" alignItems="center">
            <List
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0,
                overflowX: "auto",
                padding: 0,
              }}
            >
              {familyUserNames}
            </List>

            {isLargeScreen && (
              <Box sx={{ ml: 2 }}>
                <Tooltip title={isPresentationMode ? "יציאה ממצב תצוגה" : "מצב תצוגה"}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePresentationMode();
                    }}
                    size="large"
                    sx={{
                      color: isPresentationMode ? theme.palette.primary.main : theme.palette.text.secondary,
                      padding: "10px",
                    }}
                  >
                    {isPresentationMode ? (
                      <PauseCircleOutlineIcon fontSize="large" />
                    ) : (
                      <PresentationIcon fontSize="large" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Grid>
        </>
      )}

   {isDrawerOpen && Boolean(id) && contacts.length > 0 && (
      viewedType === "FAMILY" ? (
        <ConsultingContactInfo
          open={isDrawerOpen}
          onClose={closeDrawer}
          contacts={contacts}
          viewedType="FAMILY"
          allMimshakData={allMimshakData}
        />
      ) : (
        <ContactDetailsDrawer
          open={isDrawerOpen}
          onClose={closeDrawer}
          viewedType={viewedType}
          id={id}
          name={name}
          contacts={contacts}
          mimshaks={mimshaks}
          familyInfo={familyInfo}
          selectedPeriodType={selectedPeriodType}
        />
      )
    )}
    </Grid>
  );
};
